import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const thisMonth = () => new Date().toISOString().slice(0, 7);

// What NPD spent a month on, per customer and per project: issued INTO the NPD
// godown by the store, versus consumed OUT of it against the cost centre. SAP
// itself cannot answer this - a cost-centre confirmation carries no customer
// and one 120-char remark - so both halves are grouped here instead.
export const NpdActivityReportTab = ({ sites }) => {
  const [month, setMonth] = useState(thisMonth());
  const [siteId, setSiteId] = useState("all");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (m, s) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/report`, {
        params: { month: m, site_id: s === "all" ? "" : s },
      });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(month, siteId); }, [month, siteId, load]);

  const downloadExcel = () => {
    const rows = [["Customer", "Customer ID", "New Customer", "Project", "Product", "Description", "Issued Qty", "Consumed Qty", "Unit"]];
    (report?.rows || []).forEach((g) => {
      const products = new Set([...g.issued.map((r) => r.product_id), ...g.consumed.map((r) => r.product_id)]);
      products.forEach((pid) => {
        const issued = g.issued.find((r) => r.product_id === pid);
        const consumed = g.consumed.find((r) => r.product_id === pid);
        rows.push([
          g.customer_name || "", g.customer_id || "", g.new_customer_name || "", g.project,
          pid, (issued || consumed)?.description || "",
          issued?.qty ?? "", consumed?.qty ?? "", formatUnit((issued || consumed)?.uom) || "",
        ]);
      });
    });
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet["!cols"] = [{ wch: 34 }, { wch: 12 }, { wch: 26 }, { wch: 36 }, { wch: 16 }, { wch: 30 }, { wch: 12 }, { wch: 13 }, { wch: 8 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "NPD Activity");
    XLSX.writeFile(book, `NPD_Activity_${report?.month || month}${siteId === "all" ? "" : `_${siteId}`}.xlsx`);
  };

  return (
    <div className="space-y-5" data-testid="npd-report-tab">
      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs font-bold text-[#344054]">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="npd-report-month-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-bold text-[#344054]">Site</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger className="h-9 w-32 bg-white" data-testid="npd-report-site-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all" data-testid="npd-report-site-option-all">All sites</SelectItem>
                {sites.map((s) => <SelectItem key={s} value={s} data-testid={`npd-report-site-option-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-[#667085]" data-testid="npd-report-summary">
              {report ? `${report.customers} customer(s) \u00b7 ${report.projects} project(s)` : "\u2014"}
            </span>
            <Button variant="outline" size="sm" className="h-8 bg-white" onClick={() => load(month, siteId)} data-testid="npd-report-refresh-button">
              <ArrowClockwise size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" className="h-8 bg-white" disabled={!report?.rows?.length} onClick={downloadExcel} data-testid="npd-report-excel-button">
              <DownloadSimple size={14} className="mr-1.5" /> Excel
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {loading && <p className="text-xs text-[#98A2B3]" data-testid="npd-report-loading">Building the report...</p>}
          {!loading && (report?.rows || []).length === 0 && (
            <p className="text-xs text-[#98A2B3] py-8 text-center" data-testid="npd-report-empty">
              No NPD activity in {month}{siteId === "all" ? "" : ` at ${siteId}`}.
            </p>
          )}
          {(report?.rows || []).map((g, i) => {
            const products = Array.from(new Set([...g.issued.map((r) => r.product_id), ...g.consumed.map((r) => r.product_id)])).sort();
            return (
              <div key={`${g.customer_id}-${g.project}-${i}`} className="border border-[#D0D5DD] rounded-sm" data-testid={`npd-report-group-${i}`}>
                <div className="px-3 py-2 bg-[#F9FAFB] border-b border-[#EAECF0] flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-bold text-[#101828]" data-testid={`npd-report-customer-${i}`}>
                    {g.customer_name || "(no customer recorded)"}
                    {g.customer_id ? <span className="font-mono font-normal text-[#667085]"> &middot; {g.customer_id}</span> : null}
                  </span>
                  {g.new_customer_name ? (
                    <Badge variant="outline" className="text-[10px] bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" data-testid={`npd-report-new-customer-${i}`}>
                      new: {g.new_customer_name}
                    </Badge>
                  ) : null}
                  <span className="text-xs text-[#475467]" data-testid={`npd-report-project-${i}`}>
                    Project: <strong className="text-[#101828]">{g.project}</strong>
                    {g.project_status ? <span className="text-[#667085]"> &middot; {g.project_status}</span> : null}
                    {g.product_line ? <span className="text-[#667085]"> &middot; {g.product_line}</span> : null}
                    {g.project_owner ? <span className="text-[#98A2B3]"> &middot; {g.project_owner}</span> : null}
                  </span>
                  {g.sap_project_id ? (
                    <Badge variant="outline" className="text-[10px] bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" data-testid={`npd-report-sap-project-${i}`}>
                      SAP task {g.sap_task_id || "not set"}
                    </Badge>
                  ) : null}
                  {!g.project_id ? (
                    <Badge variant="outline" className="text-[10px] bg-[#F2F4F7] text-[#475467] border-[#D0D5DD]" data-testid={`npd-report-legacy-project-${i}`}>
                      typed, before the project list
                    </Badge>
                  ) : null}
                  <span className="text-[11px] text-[#667085] ml-auto">
                    {g.issued_requests.length} request(s) &middot; {g.consumption_postings.length} consumption posting(s)
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead className="bg-white">
                      <tr>
                        {["Product", "Description", "Issued Into NPD", "Consumed From NPD", "Still In NPD"].map((h) => (
                          <th key={h} className="px-3 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((pid) => {
                        const issued = g.issued.find((r) => r.product_id === pid);
                        const consumed = g.consumed.find((r) => r.product_id === pid);
                        const unit = formatUnit((issued || consumed)?.uom);
                        const balance = (issued?.qty || 0) - (consumed?.qty || 0);
                        const awaitingStore = issued && !issued.qty && g.issued_requests.some((r) => r.status !== "resolved" && r.status !== "cancelled");
                        return (
                          <tr key={pid} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-report-row-${i}-${pid}`}>
                            <td className="px-3 py-1.5 font-mono font-bold">{pid}</td>
                            <td className="px-3 py-1.5 text-[#475467]">{(issued || consumed)?.description || "\u2014"}</td>
                            <td className="px-3 py-1.5 tabular-nums">
                              {!issued ? "\u2014" : awaitingStore ? <span className="text-[#B54708]">awaiting store</span> : `${formatQty(issued.qty)} ${unit}`}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums">{consumed ? `${formatQty(consumed.qty)} ${unit}` : "\u2014"}</td>
                            <td className={`px-3 py-1.5 tabular-nums ${balance < 0 ? "text-[#B42318]" : "text-[#475467]"}`}>
                              {formatQty(Math.round(balance * 10000) / 10000)} {unit}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
